import { useId } from 'react'
import { COLORS, TINTS, type Look } from '../../../shared/shop'

/**
 * Pichi — the pichón. The course's mascot and its whole idea in one drawing:
 * an egg that hatches, grows feathers, and flies out of the nest.
 *
 * `stage` follows the student's level (0 egg → 1 hatching → 2 chick → 3 grad).
 * `mood` reacts to what just happened, and it is never sad: a wrong answer
 * gets "think", not a frown. `look` is what the student dressed Pichi in with
 * their XP.
 *
 * Drawn, not emoji (CLAUDE.md rule 13): it looks the same on every phone and
 * can animate.
 */

/** Level index → how grown Pichi is: Huevo egg, Pichón hatching, Volantón/Vuela solo chick, Adulto graduated. */
export function stageFor(levelIndex: number): number {
  return [0, 1, 2, 2, 3, 3][Math.max(0, Math.min(5, levelIndex))] ?? 3
}

export type Mood = 'happy' | 'cheer' | 'think' | 'wow' | 'wave' | 'look'

const INK = '#1b1030'

function Eyes({ mood }: { mood: Mood }) {
  if (mood === 'cheer') {
    return (
      <g fill="none" stroke={INK} strokeWidth="4" strokeLinecap="round">
        <path d="M44 56q6-7 12 0" />
        <path d="M64 56q6-7 12 0" />
      </g>
    )
  }
  if (mood === 'look') {
    // Glancing over its shoulder, toward the front of the room.
    return (
      <g>
        <circle cx="50" cy="56" r="8" fill="#fff" stroke={INK} strokeWidth="2.5" />
        <circle cx="70" cy="56" r="8" fill="#fff" stroke={INK} strokeWidth="2.5" />
        <circle className="pupil" cx="45.5" cy="53" r="4" fill={INK} />
        <circle className="pupil" cx="65.5" cy="53" r="4" fill={INK} />
      </g>
    )
  }
  // White eyeballs, black pupils — the friendly look. "think" glances up,
  // "wow" goes wide.
  const r = mood === 'wow' ? 9.5 : 8
  const pr = mood === 'wow' ? 4.5 : 4
  const dx = mood === 'think' ? 2.5 : 0
  const dy = mood === 'think' ? -3 : 0.5
  return (
    <g>
      <circle cx="50" cy="56" r={r} fill="#fff" stroke={INK} strokeWidth="2.5" />
      <circle cx="70" cy="56" r={r} fill="#fff" stroke={INK} strokeWidth="2.5" />
      <circle cx={50 + dx} cy={56 + dy} r={pr} fill={INK} />
      <circle cx={70 + dx} cy={56 + dy} r={pr} fill={INK} />
      <circle cx={51.5 + dx} cy={54.5 + dy} r="1.3" fill="#fff" />
      <circle cx={71.5 + dx} cy={54.5 + dy} r="1.3" fill="#fff" />
    </g>
  )
}

function Beak({ mood }: { mood: Mood }) {
  if (mood === 'cheer' || mood === 'wow') {
    return (
      <g stroke={INK} strokeWidth="2.5" strokeLinejoin="round">
        <path d="M53 66h14l-7 7z" fill="#ff9f1c" />
        <path d="M54 73q6 8 12 0" fill="#ff5d73" />
      </g>
    )
  }
  return <path d="M53 66h14l-7 9z" fill="#ff9f1c" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
}

function Cheeks() {
  return (
    <g fill="#ff7aa8" opacity=".75">
      <ellipse cx="40" cy="68" rx="5.5" ry="3.5" />
      <ellipse cx="80" cy="68" rx="5.5" ry="3.5" />
    </g>
  )
}

/* ── The wardrobe ─────────────────────────────────────────────────────── */

function Hat({ id, dy }: { id?: string; dy: number }) {
  const t = `translate(0 ${dy})`
  switch (id) {
    case 'flor':
      return (
        <g transform={t} stroke={INK} strokeWidth="2">
          {[0, 72, 144, 216, 288].map((a) => (
            <ellipse key={a} cx="80" cy="30" rx="6" ry="9" fill="#ff3d2e" transform={`rotate(${a} 80 38)`} />
          ))}
          <circle cx="80" cy="38" r="4" fill="#ffc61a" />
        </g>
      )
    case 'campeon':
      // The Corona de Campeón: every badge at gold. Taller, jewelled, and it glints.
      return (
        <g transform={t} stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <path d="M34 42l-4-28 13 12 8-18 9 16 9-16 8 18 13-12-4 28z" fill="#ffcf33" />
          <path d="M34 42h52v6H34z" fill="#ef233c" />
          <circle cx="43" cy="30" r="3" fill="#1e5bff" strokeWidth="1.5" /><circle cx="60" cy="26" r="3.5" fill="#22c55e" strokeWidth="1.5" /><circle cx="77" cy="30" r="3" fill="#ff4fa3" strokeWidth="1.5" />
          <g className="pichi-bling" fill="#fff" strokeWidth=".8">
            <path d="M28 18l1.2 2.6 2.6 1.2-2.6 1.2L28 25.6l-1.2-2.6-2.6-1.2 2.6-1.2z" />
            <path d="M92 14l1 2.2 2.2 1-2.2 1-1 2.2-1-2.2-2.2-1 2.2-1z" />
          </g>
        </g>
      )
    case 'birrete':
      // Not for sale: worn when a student is called on to explain a right answer.
      return (
        <g transform={t} stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <path d="M30 32l30-13 30 13-30 13z" fill="#1e3a8a" />
          <path d="M44 38v8c9 6 23 6 32 0v-8" fill="#1e3a8a" />
          <path d="M90 32v14" fill="none" />
          <circle cx="90" cy="48" r="3.5" fill="#ffc61a" />
        </g>
      )
    case 'bandana':
      // Tied around the head like a headband, flag on the front, knot and tails on the side.
      return (
        <g transform={t} stroke={INK} strokeWidth="2.5" strokeLinejoin="round">
          <path d="M91 44l12-6-2 10zM91 46l13 4-9 6z" fill="#ef233c" />
          <path d="M29 44c10-9 52-9 62 0l-1 8c-12-7-48-7-60 0z" fill="#1e5bff" />
          <path d="M31 47c11-6 47-6 58 0" fill="none" stroke="#fff" strokeWidth="2.5" />
          <path d="M60 40l1.6 3.4 3.7.4-2.8 2.4.9 3.6-3.4-2-3.4 2 .9-3.6-2.8-2.4 3.7-.4z" fill="#fff" strokeWidth="1" />
          <circle cx="91" cy="46" r="3.5" fill="#ef233c" />
        </g>
      )
    case 'gorra':
      return (
        <g transform={t} stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <path d="M34 42c0-14 12-22 26-22s26 8 26 22z" fill="#ef233c" />
          <path d="M60 20v22" fill="none" stroke="#fff" strokeWidth="3" />
          <path d="M60 42h36c2 0 2 6-2 6H60z" fill="#1e5bff" />
          <circle cx="60" cy="20" r="3" fill="#1e5bff" />
        </g>
      )
    case 'pava':
      return (
        <g transform={t} stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <ellipse cx="60" cy="40" rx="44" ry="9" fill="#e9c46a" />
          <path d="M40 38c0-14 8-20 20-20s20 6 20 20z" fill="#f4d58d" />
          <path d="M40 34h40v5H40z" fill="#ef233c" strokeWidth="2" />
          <path d="M22 40l6 2M96 40l-6 2M34 44l3 3M86 44l-3 3" fill="none" strokeWidth="1.5" />
        </g>
      )
    case 'audifonos':
      return (
        <g transform={t} stroke={INK} strokeWidth="3">
          <path d="M28 62c0-26 14-40 32-40s32 14 32 40" fill="none" strokeWidth="6" stroke="#7c3aed" />
          <path d="M28 62c0-26 14-40 32-40s32 14 32 40" fill="none" />
          <rect x="18" y="54" width="14" height="22" rx="6" fill="#7c3aed" />
          <rect x="88" y="54" width="14" height="22" rx="6" fill="#7c3aed" />
        </g>
      )
    case 'corona':
      return (
        <g transform={t} stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <path d="M40 40l-2-20 11 10 11-14 11 14 11-10-2 20z" fill="#ffc61a" />
          <circle cx="49" cy="34" r="2.5" fill="#ef233c" strokeWidth="1.5" />
          <circle cx="60" cy="32" r="2.5" fill="#1e5bff" strokeWidth="1.5" />
          <circle cx="71" cy="34" r="2.5" fill="#22c55e" strokeWidth="1.5" />
        </g>
      )
    default:
      return null
  }
}

function Glasses({ id }: { id?: string }) {
  const tint = (id && TINTS[id]) ?? '#ff4fa3'
  switch (id?.split('-')[0]) {
    case 'nerd':
      return (
        <g fill="none" stroke={INK} strokeWidth="3">
          <circle cx="50" cy="56" r="10" /><circle cx="70" cy="56" r="10" /><path d="M60 56h0" />
          <path d="M40 54l-8-3M80 54l8-3" />
        </g>
      )
    case 'gafas':
      return (
        <g stroke={INK} strokeWidth="2.5" strokeLinejoin="round">
          <path d="M37 50h46v4c0 8-4 12-10 12s-9-4-9-10h-8c0 6-3 10-9 10s-10-4-10-12z" fill="#1b1030" />
          <path d="M42 54l6-2M66 54l6-2" stroke="#fff" strokeWidth="2" opacity=".7" />
        </g>
      )
    case 'persianas':
      // 80s shutter shades: neon frames with horizontal slats.
      return (
        <g strokeLinejoin="round">
          <path d="M36 48h48v14c0 3-2 5-5 5H65c-3 0-5-2-5-5 0 3-2 5-5 5H41c-3 0-5-2-5-5z" fill={tint} stroke={INK} strokeWidth="2.5" />
          <path d="M39 49.5h16" stroke="#fff" strokeWidth="1.5" opacity=".7" />
          <g stroke={INK} strokeWidth="2.2">
            <path d="M39 52.5h18M39 57h18M39 61.5h18M63 52.5h18M63 57h18M63 61.5h18" />
          </g>
          <path d="M36 50l-7-3M84 50l7-3" stroke={INK} strokeWidth="2.5" />
        </g>
      )
    case 'corazon':
      return (
        <g stroke={INK} strokeWidth="2.5" strokeLinejoin="round" fill={tint}>
          <path d="M50 66c-8-5-12-9-12-14a6 6 0 0 1 12-2 6 6 0 0 1 12 2c0 5-4 9-12 14z" />
          <path d="M70 66c-8-5-12-9-12-14a6 6 0 0 1 12-2 6 6 0 0 1 12 2c0 5-4 9-12 14z" />
        </g>
      )
    default:
      return null
  }
}

function Extra({ id, dy }: { id?: string; dy: number }) {
  const t = `translate(0 ${dy})`
  switch (id) {
    case 'pajarita':
      return (
        <g transform={t} stroke={INK} strokeWidth="2.5" strokeLinejoin="round">
          <path d="M60 88l-12-7v14zM60 88l12-7v14z" fill="#ef233c" />
          <circle cx="60" cy="88" r="3.5" fill="#ef233c" />
        </g>
      )
    case 'cadena':
      // Chunky links, a big medallion with the PR star, and sparkles.
      return (
        <g transform={t}>
          <g fill="#ffcf33" stroke={INK} strokeWidth="1.6">
            {[[36, 78], [41, 82.5], [46.5, 85.5], [52.5, 87.5], [67.5, 87.5], [73.5, 85.5], [79, 82.5], [84, 78]].map(([x, y], k) => (
              <ellipse key={k} cx={x} cy={y} rx="3.4" ry="2.4" transform={`rotate(${k < 4 ? 30 - k * 8 : -6 - (k - 4) * 8} ${x} ${y})`} />
            ))}
          </g>
          <circle cx="60" cy="94" r="10" fill="#ffcf33" stroke={INK} strokeWidth="2.5" />
          <circle cx="60" cy="94" r="6.5" fill="#1e5bff" stroke={INK} strokeWidth="1.5" />
          <path d="M60 89.5l1.3 2.8 3 .3-2.3 2 .7 3-2.7-1.6-2.7 1.6.7-3-2.3-2 3-.3z" fill="#fff" />
          <g className="pichi-bling" fill="#fff" stroke={INK} strokeWidth=".8">
            <path d="M72 90l1.2 2.6 2.6 1.2-2.6 1.2L72 97.6l-1.2-2.6-2.6-1.2 2.6-1.2z" />
            <path d="M47 84l.9 1.9 1.9.9-1.9.9-.9 1.9-.9-1.9-1.9-.9 1.9-.9z" />
            <path d="M66 101l.8 1.6 1.6.8-1.6.8-.8 1.6-.8-1.6-1.6-.8 1.6-.8z" />
          </g>
        </g>
      )
    default:
      return null
  }
}

/* ── Hair: a back layer (behind the head) and a front layer (over it) ───── */

const HAIR_COLORS: Record<string, string> = { afro: '#3b2314', rizos: '#6b3e1e', trenzas: '#2a1a10', mullet: '#c8741e', cresta: '#22c55e', peluca: '#ff7ac6' }

function HairBack({ id, dy }: { id?: string; dy: number }) {
  const f = id ? HAIR_COLORS[id] : undefined
  const t = `translate(0 ${dy})`
  switch (id) {
    case 'afro':
      return (
        <g transform={t} fill={f} stroke={INK} strokeWidth="3">
          {[[34, 36, 14], [48, 22, 15], [64, 18, 16], [80, 24, 15], [90, 40, 13], [28, 52, 11], [94, 56, 11]].map(([x, y, r], k) => <circle key={k} cx={x} cy={y} r={r} />)}
        </g>
      )
    case 'rizos':
      return (
        <g transform={t} fill={f} stroke={INK} strokeWidth="2.5">
          {[[38, 34, 9], [50, 26, 10], [63, 23, 10], [76, 27, 10], [86, 37, 9]].map(([x, y, r], k) => <circle key={k} cx={x} cy={y} r={r} />)}
        </g>
      )
    case 'trenzas':
      return (
        <g transform={t} fill={f} stroke={INK} strokeWidth="2.2">
          {[0, 1, 2, 3, 4, 5].map((k) => <ellipse key={`l${k}`} cx={28 - k * .5} cy={52 + k * 8} rx="5.5" ry="5" />)}
          {[0, 1, 2, 3, 4, 5].map((k) => <ellipse key={`r${k}`} cx={92 + k * .5} cy={52 + k * 8} rx="5.5" ry="5" />)}
          <circle cx="26" cy="102" r="3.5" fill="#ff4fa3" /><circle cx="94" cy="102" r="3.5" fill="#ff4fa3" />
        </g>
      )
    case 'mullet':
      return (
        <g transform={t} fill={f} stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <path d="M30 56c-4 14-2 26 6 34l6-10c-4-8-5-16-4-24zM90 56c4 14 2 26-6 34l-6-10c4-8 5-16 4-24z" />
        </g>
      )
    case 'peluca':
      return (
        <g transform={t} fill={f} stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <path d="M26 60c-6-30 12-44 34-44s40 14 34 44c4 16 2 30-4 40-6-6-10-16-10-26H40c0 10-4 20-10 26-6-10-8-24-4-40z" />
        </g>
      )
    default:
      return null
  }
}

function HairFront({ id, dy }: { id?: string; dy: number }) {
  const f = id ? HAIR_COLORS[id] : undefined
  const t = `translate(0 ${dy})`
  switch (id) {
    case 'cresta':
      return <path transform={t} d="M48 38l-2-14 7 7 1-16 6 13 4-15 3 16 7-10-1 15 6-5-3 11z" fill={f} stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
    case 'rizos':
      return (
        <g transform={t} fill={f} stroke={INK} strokeWidth="2.2">
          <circle cx="46" cy="40" r="6" /><circle cx="56" cy="37" r="6" /><circle cx="66" cy="37" r="6" /><circle cx="75" cy="40" r="6" />
        </g>
      )
    case 'trenzas':
      return <path transform={t} d="M31 50c2-16 14-24 29-24s27 8 29 24c-8-8-18-11-29-11s-21 3-29 11z" fill={f} stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
    case 'mullet':
      return <path transform={t} d="M32 48c2-14 14-20 28-20s26 6 28 20c-6-4-10-4-14-2-4-4-10-4-14 0-4-4-10-4-14 0-4-2-8-2-14 2z" fill={f} stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
    case 'peluca':
      return <path transform={t} d="M32 50c0-14 12-24 28-24s28 10 28 24c-4-3-8-3-11 0-3-4-7-4-10 0-3-4-7-4-10 0-3-4-7-4-10 0-3-3-7-3-15 0z" fill={f} stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
    default:
      return null
  }
}

/* ── Rides: Pichi sits IN them. A back layer, and a front that hides the feet ── */

function RideBack({ id }: { id?: string }) {
  switch (id) {
    case 'lancha':
      return (
        <g stroke={INK} strokeWidth="2.5" strokeLinejoin="round">
          <path d="M100 96V30" fill="none" strokeWidth="3" />
          <path d="M100 32l14 5-14 5z" fill="#ef233c" />
          <path d="M100 30q12 20 10 52" fill="none" strokeWidth="1.2" strokeDasharray="2 2" />
        </g>
      )
    case 'fiesta':
      return (
        <g stroke={INK} strokeWidth="2">
          <path d="M8 40q52-22 104 0" fill="none" strokeWidth="1.8" />
          {[[16, 36, '#ef233c'], [30, 31, '#ffc61a'], [44, 28, '#22c55e'], [58, 27, '#38bdf8'], [72, 28, '#ff4fa3'], [86, 31, '#a855f7'], [100, 36, '#ff7a1a']].map(([x, y, c], k) => (
            <circle key={k} className="pichi-bulb" cx={x as number} cy={y as number} r="3.5" fill={c as string} style={{ animationDelay: `${k * 0.15}s` }} />
          ))}
        </g>
      )
    case 'motora':
      return (
        <g stroke={INK} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round">
          <path d="M84 62l10-10M90 50l10 2" fill="none" />
        </g>
      )
    case 'fourtrack':
      return (
        <g stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <path d="M82 70l8-8h10" fill="none" />
          <path d="M14 78h22v-6H14z" fill="#3f3f46" />
        </g>
      )
    default:
      return null
  }
}

function RideFront({ id }: { id?: string }) {
  switch (id) {
    case 'jetski':
      // Side view, heading right: pointed nose, a seat behind Pichi, handlebars in front, spray off the stern.
      return (
        <g stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <path d="M82 86l6-18M84 68h12" fill="none" strokeWidth="3.5" strokeLinecap="round" />
          <path d="M4 90c0-4 3-6 8-6h66l10-4c12 0 24 4 30 12-4 6-12 12-24 12H16C9 104 4 98 4 90z" fill="#ef233c" />
          <path d="M10 94h96" fill="none" stroke="#fff" strokeWidth="3" />
          <path d="M14 99h78" fill="none" stroke="#ffc61a" strokeWidth="2.5" />
          <path d="M8 84c0-6 6-10 14-10h14c4 0 6 3 6 6v4z" fill="#1b1030" />
          <path d="M92 82l14 4" fill="none" stroke="#7dd3fc" strokeWidth="3" />
          <path className="pichi-spray" d="M2 92c-6-2-10-8-8-14M4 98c-8 0-14-4-15-10M6 102c-6 3-12 2-16-2" fill="none" stroke="#7dd3fc" strokeWidth="2.5" strokeLinecap="round" />
          <path className="pichi-waves" d="M-4 110q8-5 16 0t16 0 16 0 16 0 16 0 16 0 16 0 16 0" fill="none" stroke="#00c2c7" strokeWidth="3.5" />
        </g>
      )
    case 'surf':
      return (
        <g stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <path d="M26 110c-14 0-10-10 4-12h72c12 0 18 4 18 7s-6 5-18 5z" fill="#fff" />
          <path d="M30 104h80" fill="none" stroke="#ff4fa3" strokeWidth="4" />
          <path d="M44 104h10M72 104h10" fill="none" stroke="#1e5bff" strokeWidth="4" />
          <path d="M38 110l-4 8 10-8" fill="#ff4fa3" strokeWidth="2" />
          <path className="pichi-waves" d="M-4 116q8-6 16 0t16 0 16 0 16 0 16 0 16 0 16 0 16 0" fill="none" stroke="#00c2c7" strokeWidth="4" />
          <path d="M104 98c8-8 14-6 16-2-5-1-8 1-9 5" fill="#7dd3fc" strokeWidth="2" />
        </g>
      )
    case 'dirtbike':
      return (
        <g stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <circle cx="20" cy="104" r="13" fill="#27272a" /><circle cx="20" cy="104" r="5" fill="#f97316" />
          <circle cx="100" cy="104" r="13" fill="#27272a" /><circle cx="100" cy="104" r="5" fill="#f97316" />
          <g stroke="#52525b" strokeWidth="1.5" fill="none">
            <circle cx="20" cy="104" r="9.5" strokeDasharray="3 3" /><circle cx="100" cy="104" r="9.5" strokeDasharray="3 3" />
          </g>
          <path d="M20 104l18-16h40l22 16" fill="none" strokeWidth="4" />
          <path d="M34 86l10-6h36l14 6-4 8H40z" fill="#f97316" />
          <path d="M98 86l6-16M100 70h10" fill="none" />
          <text x="62" y="92" textAnchor="middle" fontSize="8" fontWeight="900" fill="#fff" stroke="none" fontFamily="Fredoka Variable, sans-serif">42</text>
          <path className="pichi-dust" d="M4 112c-6-2-10 0-12 3M10 116c-6 0-8 3-9 5" fill="none" stroke="#a16207" strokeWidth="2.5" opacity=".6" />
        </g>
      )
    case 'lancha':
      return (
        <g stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <path d="M6 84h108l-12 24H20z" fill="#f8fafc" />
          <path d="M9 90h102" fill="none" stroke="#1e5bff" strokeWidth="5" />
          <text x="60" y="103" textAnchor="middle" fontSize="9" fontWeight="900" fill="#1e5bff" stroke="none" fontFamily="Fredoka Variable, sans-serif">LA PICHI</text>
          <path className="pichi-waves" d="M-4 110q8-5 16 0t16 0 16 0 16 0 16 0 16 0 16 0 16 0" fill="none" stroke="#00c2c7" strokeWidth="3.5" />
        </g>
      )
    case 'fiesta':
      return (
        <g stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <path d="M4 82h112l-10 26H14z" fill="#ff4fa3" />
          <path d="M8 90h104" fill="none" stroke="#ffc61a" strokeWidth="4" />
          <path d="M8 97h104" fill="none" stroke="#00c2c7" strokeWidth="4" />
          <g fill="#fff" stroke={INK} strokeWidth="1.5"><circle cx="24" cy="102" r="3" /><circle cx="96" cy="102" r="3" /></g>
          <path d="M104 70v12M104 70l8 3-8 3" fill="#ffc61a" strokeWidth="2" />
          <path className="pichi-waves" d="M-4 111q8-5 16 0t16 0 16 0 16 0 16 0 16 0 16 0 16 0" fill="none" stroke="#00c2c7" strokeWidth="3.5" />
        </g>
      )
    case 'motora':
      return (
        <g stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <circle cx="22" cy="104" r="12" fill="#27272a" /><circle cx="22" cy="104" r="4.5" fill="#d4d4d8" />
          <circle cx="98" cy="104" r="12" fill="#27272a" /><circle cx="98" cy="104" r="4.5" fill="#d4d4d8" />
          <path d="M22 104l16-18h44l16 18" fill="none" strokeWidth="4" />
          <path d="M30 84h60c4 0 6 3 5 6l-3 8H33l-6-8c-1-3 0-6 3-6z" fill="#ef233c" />
          <path d="M36 88h48" fill="none" stroke="#fff" strokeWidth="2.5" />
          <circle cx="104" cy="82" r="4" fill="#fde047" />
        </g>
      )
    case 'fourtrack':
      return (
        <g stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <rect x="6" y="92" width="26" height="24" rx="10" fill="#27272a" />
          <rect x="88" y="92" width="26" height="24" rx="10" fill="#27272a" />
          <path d="M10 98h18M10 104h18M10 110h18M92 98h18M92 104h18M92 110h18" stroke="#52525b" strokeWidth="2" />
          <path d="M20 84h80l6 12H14z" fill="#16a34a" />
          <path d="M26 88h68" fill="none" stroke="#bbf7d0" strokeWidth="2.5" />
          <path d="M24 96c10 4 62 4 72 0" fill="none" stroke="#7c4a1e" strokeWidth="3" opacity=".6" />
        </g>
      )
    default:
      return null
  }
}

export default function Pichi({ stage = 2, mood = 'happy', size = 120, look, className }: {
  stage?: number
  mood?: Mood
  size?: number
  look?: Look
  className?: string
}) {
  const s = Math.max(0, Math.min(3, stage))
  const wingsUp = mood === 'cheer' || mood === 'wave'
  // React 19's ids contain « » (older ones ':'), which break url(#…) — keep only safe characters.
  const gid = `p${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const base = COLORS[look?.color ?? 'amarillo'] ?? COLORS.amarillo!
  // Rainbow and gold are drawn with gradients; everything else is flat.
  const c = look?.color === 'arcoiris'
    ? { body: `url(#${gid}-rb)`, wing: `url(#${gid}-rw)`, belly: '#ffffff' }
    : look?.color === 'oro'
      ? { body: `url(#${gid}-gold)`, wing: `url(#${gid}-goldw)`, belly: '#e8b400' }
      : base
  const hatDy = s === 0 ? -14 : 0
  return (
    <svg
      className={['pichi', `pichi-${mood}`, className].filter(Boolean).join(' ')}
      width={size} height={size} viewBox="0 0 120 120" aria-hidden="true"
    >
      <defs>
        <linearGradient id={`${gid}-rb`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#ff5a5f" /><stop offset=".25" stopColor="#ffc61a" /><stop offset=".5" stopColor="#4ade80" />
          <stop offset=".75" stopColor="#38bdf8" /><stop offset="1" stopColor="#a78bfa" />
        </linearGradient>
        <linearGradient id={`${gid}-rw`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f472b6" /><stop offset="1" stopColor="#60a5fa" />
        </linearGradient>
        <radialGradient id={`${gid}-gold`} cx=".35" cy=".3" r=".8">
          <stop offset="0" stopColor="#fffbe0" /><stop offset=".25" stopColor="#ffe066" /><stop offset=".55" stopColor="#f5b800" /><stop offset=".8" stopColor="#c98a00" /><stop offset="1" stopColor="#8a5a00" />
        </radialGradient>
        <linearGradient id={`${gid}-goldw`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff1a8" /><stop offset=".5" stopColor="#e0a100" /><stop offset="1" stopColor="#9a6700" />
        </linearGradient>
        <linearGradient id={`${gid}-shine`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#fff" stopOpacity="0" /><stop offset=".5" stopColor="#fff" stopOpacity=".75" /><stop offset="1" stopColor="#fff" stopOpacity="0" />
          <animateTransform attributeName="gradientTransform" type="translate" values="-1 0; 1.2 0" dur="2.2s" repeatCount="indefinite" />
        </linearGradient>
      </defs>
      {!look?.ride && <ellipse cx="60" cy="112" rx="30" ry="5" fill={INK} opacity=".15" />}
      <RideBack id={look?.ride} />
      {look?.extra === 'capa' && (
        <g className="pichi-cape" stroke={INK} strokeWidth="3" strokeLinejoin="round">
          <path d="M40 60c-14 20-20 36-18 50 12-4 20 0 28-4 6 4 14 4 20 0 8 4 16 0 28 4 2-14-4-30-18-50z" fill="#ef233c" />
          <path d="M36 94c8-2 14 0 20-2M64 92c6 2 12 0 20 2" fill="none" stroke="#ffc61a" strokeWidth="2.5" />
        </g>
      )}

      <g className="pichi-body">
        <HairBack id={look?.hair} dy={hatDy} />
        {s === 0 ? (
          <>
            <path d="M60 14c20 0 34 30 34 54 0 22-15 38-34 38S26 90 26 68c0-24 14-54 34-54z" fill="#fff6dd" stroke={INK} strokeWidth="3.5" />
            <g fill={c.body} opacity=".85">
              <circle cx="42" cy="44" r="4" /><circle cx="78" cy="40" r="3" /><circle cx="80" cy="84" r="4.5" /><circle cx="38" cy="88" r="3" />
            </g>
            <Eyes mood={mood} />
            <Cheeks />
            {(mood === 'cheer' || mood === 'wow')
              ? <path d="M54 74q6 6 12 0" fill="none" stroke={INK} strokeWidth="3" strokeLinecap="round" />
              : <path d="M55 73q5 4 10 0" fill="none" stroke={INK} strokeWidth="3" strokeLinecap="round" />}
          </>
        ) : (
          <>
            <g className={wingsUp ? 'pichi-wings up' : 'pichi-wings'} fill={c.wing} stroke={INK} strokeWidth="3.5" strokeLinejoin="round">
              <path className="wing-l" d="M30 72c-12-2-18-12-16-22 8 2 16 8 20 16z" />
              <path className="wing-r" d="M90 72c12-2 18-12 16-22-8 2-16 8-20 16z" />
            </g>
            <ellipse cx="60" cy="68" rx="32" ry="34" fill={c.body} stroke={INK} strokeWidth="3.5" />
            <ellipse cx="60" cy="80" rx="18" ry="16" fill={c.belly} />
            {/* Gloss: every Pichi gets a shine. */}
            <ellipse cx="46" cy="48" rx="9" ry="6" fill="#fff" opacity=".35" transform="rotate(-30 46 48)" />
            {look?.color === 'oro' && (
              <g className="pichi-glitter">
                <ellipse cx="60" cy="68" rx="30" ry="32" fill={`url(#${gid}-shine)`} />
                <g fill="#fff">
                  {([[42, 52, 1.6], [76, 48, 1.3], [80, 80, 1.8], [40, 84, 1.2], [58, 92, 1.4], [70, 62, 1]] as const).map(([x, y, r], k) => (
                    <path key={k} className="glint" style={{ animationDelay: `${k * 0.3}s` }}
                      d={`M${x} ${y - r * 3}l${r} ${r * 2} ${r * 2} ${r}-${r * 2} ${r}-${r} ${r * 2}-${r}-${r * 2}-${r * 2}-${r} ${r * 2}-${r}z`} />
                  ))}
                </g>
              </g>
            )}
            {!look?.hat && !look?.hair && <path d="M56 35c-2-8 2-12 6-12-1 4 1 6 4 7-4 1-6 3-6 6" fill={c.body} stroke={INK} strokeWidth="3" strokeLinejoin="round" />}
            <Eyes mood={mood} />
            <Beak mood={mood} />
            <Cheeks />
            {s >= 2 && !look?.ride && (
              <g stroke="#ff9f1c" strokeWidth="4" strokeLinecap="round">
                <path d="M50 101v7M46 108h8" /><path d="M70 101v7M66 108h8" />
              </g>
            )}
            {s === 1 && (
              <path d="M26 78l8-8 8 8 9-9 9 9 9-9 9 9 8-8 8 8v8c0 14-15 24-34 24S26 100 26 86z" fill="#fff6dd" stroke={INK} strokeWidth="3.5" strokeLinejoin="round" />
            )}
            {s === 3 && !look?.hat && (
              <g stroke={INK} strokeWidth="3" strokeLinejoin="round">
                <path d="M34 30l26-11 26 11-26 11z" fill="#1e3a8a" />
                <path d="M46 35v8c8 5 20 5 28 0v-8" fill="#1e3a8a" />
                <path d="M86 30v12" fill="none" />
                <circle cx="86" cy="44" r="3" fill="#ffc61a" />
              </g>
            )}
          </>
        )}
        {/* Hatching: the neck is just above the shell, so extras sit higher. */}
        <HairFront id={look?.hair} dy={hatDy} />
        {look?.extra !== 'capa' && !look?.ride && <Extra id={look?.extra} dy={s === 0 ? 2 : s === 1 ? -8 : 0} />}
        <Glasses id={look?.glasses} />
        <Hat id={look?.hat} dy={hatDy} />
      </g>
      <RideFront id={look?.ride} />
      {/* Riding: bling goes in front of the ride so it still shows, lifted a little. */}
      {look?.ride && look.extra !== 'capa' && <Extra id={look.extra} dy={s === 1 ? -14 : -6} />}

      {mood === 'think' && (
        <g className="pichi-bubble">
          <circle cx="98" cy="20" r="13" fill="#fff" stroke={INK} strokeWidth="3" />
          <text x="98" y="26" textAnchor="middle" fontSize="18" fontWeight="800" fill={INK} fontFamily="Fredoka Variable, sans-serif">?</text>
        </g>
      )}
      {mood === 'cheer' && (
        <g className="pichi-sparks" fill="#ffc61a" stroke={INK} strokeWidth="2">
          <path d="M14 22l3 7 7 3-7 3-3 7-3-7-7-3 7-3z" />
          <path d="M104 14l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" />
        </g>
      )}
    </svg>
  )
}

/**
 * The end-of-lesson bird — ONE component for the student's phone and the
 * teacher's console, so the two can't drift apart. Never the egg (an egg
 * bouncing on a celebration screen read as a glitch), and a gentle bob
 * rather than the answer-screen hop.
 */
export function EndBird({ level, look }: { level: number; look?: Look }) {
  return (
    <div className="end-bird">
      <Pichi size={150} stage={Math.max(2, stageFor(level))} mood="happy" look={look} />
    </div>
  )
}
